import { Control, Controller, UseFormWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import FileUploaderStatus from "~/modules/media/components/FileUploaderStatus";
import FormTemplateDialogEditor from "~/shared/components/FormTemplate/FormTemplateDialogEditor";
import FormTemplateForm from "~/shared/components/FormTemplate/FormTemplateForm";
import { Shared_FormTemplateFragment } from "~/shared/components/FormTemplate/fragments/fragments.generated";
import SpeciesSelect from "~/shared/components/SpeciesSelect";

import { ObservationMediaDisplay } from "../../ObservationMediaDisplay";
import { MapEditorEcologicalFunctionPanelEcologicalFunctionFragment } from "../fragments.generated";
import { EcologicalFunctionUpdateFormValues } from "../useUpdateEcologicalFunctionMutation";
import EcologicalFunctionTypeSelectElement from "./EcologicalFunctionTypeSelectElement";

interface MapEditorEcologicalFunctionFormProps {
    control: Control<EcologicalFunctionUpdateFormValues, any, EcologicalFunctionUpdateFormValues>;
    ecologicalFunction: MapEditorEcologicalFunctionPanelEcologicalFunctionFragment;
    locationFormTemplate: Shared_FormTemplateFragment;
    watch: UseFormWatch<EcologicalFunctionUpdateFormValues>;
}

export function MapEditorEcologicalFunctionForm({
    control,
    ecologicalFunction,
    locationFormTemplate,
    watch,
}: MapEditorEcologicalFunctionFormProps) {
    const { t } = useTranslation("mapEditor");

    const ecologicalFunctionTypeId = watch("ecologicalFunctionTypeId");
    const ecologicalFunctionType = ecologicalFunction.researchType.ecologicalFunctionTypes.find(
        x => x.id === ecologicalFunctionTypeId,
    );

    return (
        <>
            <Controller
                control={control}
                name="ecologicalFunctionTypeId"
                render={({ field: { value, onChange } }) => (
                    <EcologicalFunctionTypeSelectElement
                        value={value}
                        onChange={onChange}
                        label={t("featureDetailEcologicalFunction.ecologicalFunctionType")}
                        disabled={false}
                        options={ecologicalFunction.researchType.ecologicalFunctionTypes}
                    />
                )}
            />

            <Controller
                control={control}
                name="speciesIds"
                render={({ field: { value, onChange } }) => (
                    <SpeciesSelect
                        label={t("featureDetailEcologicalFunction.species")}
                        value={value}
                        onChange={onChange}
                        disabled={false}
                        multiple={true}
                    />
                )}
            />

            <Controller
                control={control}
                name="medias"
                render={({ field: { value, onChange } }) => (
                    <>
                        <FileUploaderStatus
                            disabled={false}
                            onUploadFinished={mediaItems => onChange([...mediaItems, ...value])}
                        />
                        <ObservationMediaDisplay
                            media={value}
                            onDelete={deletedMediaId => {
                                onChange(value.filter(media => media.id !== deletedMediaId));
                            }}
                        />
                    </>
                )}
            />
            <Controller
                control={control}
                name="preciseLocation"
                render={({ field: { value, onChange } }) => (
                    <FormTemplateDialogEditor
                        form={locationFormTemplate}
                        values={value}
                        onChange={values => onChange(values)}
                        disabled={false}
                    />
                )}
            />
            {ecologicalFunctionType?.formTemplate && (
                <Controller
                    control={control}
                    name="answers"
                    render={({ field: { value, onChange } }) => (
                        <FormTemplateForm
                            form={ecologicalFunctionType.formTemplate}
                            values={value}
                            onChange={values => onChange(values)}
                            disabled={false}
                        />
                    )}
                />
            )}
        </>
    );
}
